import React, { useCallback, useEffect, useMemo, useState } from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import VerifiedIcon from '@mui/icons-material/Verified';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { useAuth } from '../../contexts/AuthContext';
import { getAssessmentOfflinePackage } from '../../api/assessments';
import {
  closeSrhtExam,
  createSrhtExam,
  deleteSrhtExam,
  exportSrhtExamFullChainPdf,
  finalizeSrhtPracticalSheet,
  saveSrhtOralRetake,
  setSrhtAllFinalResultsRelease,
  setSrhtPersonalFinalResultsRelease,
  finalizeSrhtWrittenExamResult,
  getSrhtExam,
  getSrhtExamReferenceOptions,
  getSrhtExams,
  saveSrhtPracticalSheet,
  saveSrhtWrittenExamResult,
  unlockSrhtExam,
  updateSrhtPracticalTimerSession,
  unlockSrhtPracticalSheet,
  unlockSrhtWrittenExamResult,
  updateSrhtExam,
} from '../../api/srhtExams';
import { getSrhtCourse } from '../../api/srhtCourses';
import CollapsibleModuleHeaderCard from '../../components/CollapsibleModuleHeaderCard';
import { loadLogbuchPdfBrandingAssets } from '../../components/pdf/logbuchPdfBuilder';
import { downloadPdfResponse, extractPdfErrorMessage } from '../../utils/pdfDownload';
import { readPersistedTab, writePersistedTab } from '../../utils/tabPersistence';
import {
  canAccessSrhtExamModule,
  canEditSrhtExamPracticalSheets,
  canEditSrhtExamWrittenResults,
  canManageSrhtExamEvents,
  canViewSrhtExamWrittenResults,
} from '../../config/roleAccess';
import {
  buildSrhtExamExportModel,
  buildSrhtExamPdfDocument,
  buildSrhtExamPdfFilename,
  getSrhtExamAssessmentSelectionCandidates,
  SRHT_EXAM_PDF_OPTIONS,
} from './srhtExamPdfExport';
import {
  isFullChainPdfVariant,
} from './srhtExamFullChainMerge';
import {
  getVisibleSrhtExamTabs,
  SRHT_EXAM_TAB_FINAL,
  SRHT_EXAM_TAB_MANAGE,
  SRHT_EXAM_TAB_REGULATION,
  SRHT_EXAM_TAB_SHEETS,
  SRHT_EXAM_TAB_WRITTEN,
} from './srhtExamTabs';
import SrhtExamRegulationPanel from './SrhtExamRegulationPanel';
import SrhtExamPracticalSheetsPanel from './SrhtExamPracticalSheetsPanel';
import SrhtExamFinalEvaluationPanel from './SrhtExamFinalEvaluationPanel';
import { getDefaultWrittenExamDate, syncWrittenExamDateWithExamDate } from './srhtExamDateUtils';

const SRHT_EXAM_LIST_CACHE_KEY = 'srht-exam-list-cache-v1';
const SRHT_EXAM_OFFLINE_PACKAGE_PREFIX = 'srht-exam-offline-package-v1';
const SRHT_EXAM_SYNC_QUEUE_KEY = 'srht-exam-sync-queue-v1';
const SRHT_EXAM_OFFLINE_MODE_ENABLED = false;
const SRHT_EXAM_HEADER_COLLAPSE_STORAGE_KEY = 'srht-exam.header.expanded.v1';
const SRHT_EXAM_ACTIVE_TAB_STORAGE_KEY = 'srht-exam.active-tab.v1';
const SRHT_EXAM_ALLOWED_TABS = [
  SRHT_EXAM_TAB_MANAGE,
  SRHT_EXAM_TAB_REGULATION,
  SRHT_EXAM_TAB_WRITTEN,
  SRHT_EXAM_TAB_SHEETS,
  SRHT_EXAM_TAB_FINAL,
];

pdfMake.addVirtualFileSystem(pdfFonts);

function normalizeMultiSelectValue(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string' && value) {
    return value.split(',');
  }
  return [];
}

function formatAssessmentSelectionLabel(event) {
  const dateLabel = event?.assessmentDate ? `, ${event.assessmentDate}` : '';
  const resultLabel = event?.qualified === true ? 'bestanden' : event?.qualified === false ? 'nicht bestanden' : 'ohne Abschluss';
  return `${event?.label || 'Eignungsverfahren'}${dateLabel} (${resultLabel})`;
}

function buildOfflinePackageKey(examId) {
  if (!examId) {
    return '';
  }

  return `${SRHT_EXAM_OFFLINE_PACKAGE_PREFIX}:${examId}`;
}

function readStoredJson(storageKey) {
  if (!storageKey || typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch {
    return null;
  }
}

function writeStoredJson(storageKey, value) {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore storage write failures.
  }
}

function readExamListCache() {
  const cachedValue = readStoredJson(SRHT_EXAM_LIST_CACHE_KEY);
  return Array.isArray(cachedValue?.exams) ? cachedValue.exams : [];
}

function writeExamListCache(exams) {
  writeStoredJson(SRHT_EXAM_LIST_CACHE_KEY, { exams: Array.isArray(exams) ? exams : [] });
}

function readOfflinePackage(examId) {
  return readStoredJson(buildOfflinePackageKey(examId));
}

function writeOfflinePackage(examId, value) {
  writeStoredJson(buildOfflinePackageKey(examId), value);
}

function readSyncQueue() {
  const cachedValue = readStoredJson(SRHT_EXAM_SYNC_QUEUE_KEY);
  return Array.isArray(cachedValue) ? cachedValue : [];
}

function writeSyncQueue(queueItems) {
  writeStoredJson(SRHT_EXAM_SYNC_QUEUE_KEY, Array.isArray(queueItems) ? queueItems : []);
}

function listPreparedOfflineExams() {
  if (typeof window === 'undefined') {
    return [];
  }

  return Object.keys(window.localStorage)
    .filter((storageKey) => storageKey.startsWith(`${SRHT_EXAM_OFFLINE_PACKAGE_PREFIX}:`))
    .map((storageKey) => readStoredJson(storageKey))
    .filter((item) => item?.detail?.exam?.id)
    .map((item) => ({
      examId: String(item.detail.exam.id),
      label: item.detail.exam.label || 'Prüfung SRHT',
      cachedAt: item.cachedAt || null,
      candidateCount: Array.isArray(item.detail.candidates) ? item.detail.candidates.length : 0,
    }))
    .sort((left, right) => String(left.label || '').localeCompare(String(right.label || ''), 'de'));
}

function buildExamForm(detail = null) {
  if (!detail) {
    return {
      label: '',
      practicalExamDate: '',
      writtenExamDate: '',
      location: '',
      notes: '',
      sourceCourseIds: [],
      candidateUserIds: [],
      chairUserId: '',
      courseLeaderUserId: '',
      examinerUserIds: [],
      personalratUserIds: [],
      department37_4UserIds: [],
      equalOpportunityOfficerUserIds: [],
      observerUserIds: [],
      observerNotes: {},
    };
  }

  const observerNotes = {};
  (detail.staff?.observers || []).forEach((observer) => {
    observerNotes[String(observer.userId)] = observer.noteText || '';
  });

  return {
    label: detail.exam?.label || '',
    practicalExamDate: detail.exam?.practical_exam_date || detail.exam?.exam_date || '',
    writtenExamDate: detail.exam?.written_exam_date || '',
    location: detail.exam?.location || '',
    notes: detail.exam?.notes || '',
    sourceCourseIds: (detail.sourceCourses || []).map((item) => Number(item.courseId)),
    candidateUserIds: (detail.candidates || []).map((item) => Number(item.userId)),
    chairUserId: detail.staff?.chair?.userId ? Number(detail.staff.chair.userId) : '',
    courseLeaderUserId: detail.staff?.courseLeader?.userId ? Number(detail.staff.courseLeader.userId) : '',
    examinerUserIds: (detail.staff?.examiners || []).map((item) => Number(item.userId)),
    personalratUserIds: (detail.staff?.personalrat || []).map((item) => Number(item.userId)),
    department37_4UserIds: (detail.staff?.department37_4 || []).map((item) => Number(item.userId)),
    equalOpportunityOfficerUserIds: (detail.staff?.equalOpportunityOfficers || []).map((item) => Number(item.userId)),
    observerUserIds: (detail.staff?.observers || []).map((item) => Number(item.userId)),
    observerNotes,
  };
}

function buildStaffPayload(userIds = [], notesByUserId = {}) {
  return (Array.isArray(userIds) ? userIds : []).map((userId) => ({
    userId: Number(userId),
    noteText: notesByUserId[String(userId)] || '',
  }));
}

function buildWrittenInputState(candidates = []) {
  return candidates.reduce((accumulator, candidate) => {
    accumulator[String(candidate.id)] = candidate.writtenResult?.points === 0 || candidate.writtenResult?.points
      ? String(candidate.writtenResult.points)
      : '';
    return accumulator;
  }, {});
}

function getGradeChipColor(grade) {
  if (grade === 1 || grade === 2) return 'success';
  if (grade === 3 || grade === 4) return 'warning';
  if (grade === 5 || grade === 6) return 'error';
  return 'default';
}

function getGradeFromPoints(pointsValue) {
  if (pointsValue === null || typeof pointsValue === 'undefined' || String(pointsValue).trim() === '') {
    return null;
  }

  const numericPoints = Number(pointsValue);
  if (!Number.isInteger(numericPoints) || numericPoints < 0 || numericPoints > 129) {
    return null;
  }
  if (numericPoints >= 119) return 1;
  if (numericPoints >= 104) return 2;
  if (numericPoints >= 86) return 3;
  if (numericPoints >= 65) return 4;
  if (numericPoints >= 39) return 5;
  return 6;
}

function getExamStatusChipColor(status) {
  if (status === 'closed') return 'success';
  if (status === 'draft') return 'warning';
  if (status === 'active') return 'primary';
  return 'default';
}

function getExamStatusLabel(status) {
  if (status === 'closed') return 'Abgeschlossen';
  if (status === 'draft') return 'Entwurf';
  if (status === 'active') return 'Aktiv';
  return String(status || 'Entwurf');
}

function createQueueItem(action, examId, candidateId, payload = {}) {
  return {
    id: `${action}:${examId}:${candidateId}:${Date.now()}`,
    action,
    examId: Number(examId),
    candidateId: Number(candidateId),
    payload,
    createdAt: new Date().toISOString(),
    lastError: '',
  };
}

export default function PruefungSrhtPage() {
  const { user } = useAuth();
  const [referenceOptions, setReferenceOptions] = useState({
    courses: [],
    courseCandidates: [],
    chairOptions: [],
    courseLeaderOptions: [],
    examinerOptions: [],
    observerOptions: [],
  });
  const [examList, setExamList] = useState(() => readExamListCache());
  const [selectedExamId, setSelectedExamId] = useState('');
  const [examDetail, setExamDetail] = useState(null);
  const [examForm, setExamForm] = useState(() => buildExamForm(null));
  const [writtenInputs, setWrittenInputs] = useState({});
  const [activeTab, setActiveTab] = useState(() => readPersistedTab(
    SRHT_EXAM_ACTIVE_TAB_STORAGE_KEY,
    SRHT_EXAM_ALLOWED_TABS,
    SRHT_EXAM_TAB_MANAGE,
  ));
  const [busy, setBusy] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [actionCandidateId, setActionCandidateId] = useState('');
  const [finalEvaluationActionCandidateId, setFinalEvaluationActionCandidateId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [syncQueueItems, setSyncQueueItems] = useState(() => readSyncQueue());
  const [offlinePreparedExams, setOfflinePreparedExams] = useState(() => listPreparedOfflineExams());
  const [unlockDialog, setUnlockDialog] = useState({ open: false, candidateId: '', reason: '' });
  const [syncBusy, setSyncBusy] = useState(false);
  const [practicalSheetAction, setPracticalSheetAction] = useState('');
  const [closeExamDialogOpen, setCloseExamDialogOpen] = useState(false);
  const [unlockExamDialogOpen, setUnlockExamDialogOpen] = useState(false);
  const [deleteExamDialogOpen, setDeleteExamDialogOpen] = useState(false);
  const [examUnlockReason, setExamUnlockReason] = useState('');
  const [selectedPdfVariant, setSelectedPdfVariant] = useState('exam-report-all');
  const [selectedPdfCandidateIds, setSelectedPdfCandidateIds] = useState([]);
  const [selectedAssessmentEventIdsByUserId, setSelectedAssessmentEventIdsByUserId] = useState({});
  const [assessmentSelectionDialogState, setAssessmentSelectionDialogState] = useState({
    open: false,
    candidates: [],
    selectionsByUserId: {},
    context: null,
  });
  const [exportingPdf, setExportingPdf] = useState(false);
  const [finalEvaluationGlobalBusy, setFinalEvaluationGlobalBusy] = useState(false);
  const [practicalEditForbidden, setPracticalEditForbidden] = useState(false);

  const canAccessModule = canAccessSrhtExamModule(user);
  const canManageAdministration = canManageSrhtExamEvents(user);
  const canViewWrittenExam = canViewSrhtExamWrittenResults(user);
  const canEditWrittenExam = canEditSrhtExamWrittenResults(user);
  const canEditPracticalSheets = canEditSrhtExamPracticalSheets(user);
  const canAccessEvaluationTabs = canViewWrittenExam || canManageAdministration || canEditPracticalSheets;
  const canViewFinalEvaluation = examDetail?.permissions?.canViewFinalEvaluation ?? canAccessEvaluationTabs;
  const visibleTabs = useMemo(() => getVisibleSrhtExamTabs({
    canAccessModule,
    canAccessEvaluationTabs,
    canViewFinalEvaluation,
    canManageAdministration,
    canViewWrittenExam,
  }), [canAccessEvaluationTabs, canAccessModule, canManageAdministration, canViewFinalEvaluation, canViewWrittenExam]);
  const practicalTimeControllerOptions = useMemo(() => {
    const uniqueOptions = new Map();

    [
      ...(examDetail?.staff?.examiners || []),
      ...(examDetail?.staff?.personalrat || []),
      ...(examDetail?.staff?.department37_4 || []),
      ...(examDetail?.staff?.equalOpportunityOfficers || []),
      ...(examDetail?.staff?.observers || []),
      ...(referenceOptions.examinerOptions || []),
      ...(referenceOptions.observerOptions || []),
    ].forEach((item) => {
      const userId = Number(item?.userId || item?.id || 0);
      if (!userId || uniqueOptions.has(String(userId))) {
        return;
      }

      uniqueOptions.set(String(userId), {
        id: userId,
        fullName: item?.fullName || '-',
      });
    });

    return Array.from(uniqueOptions.values()).sort((left, right) => (
      String(left.fullName || '').localeCompare(String(right.fullName || ''), 'de')
    ));
  }, [examDetail, referenceOptions.examinerOptions, referenceOptions.observerOptions]);
  const practicalExaminerOptions = useMemo(() => {
    const uniqueOptions = new Map();

    [
      examDetail?.staff?.chair,
      examDetail?.staff?.courseLeader,
      ...(examDetail?.staff?.examiners || []),
      ...(referenceOptions.examinerOptions || []),
    ].forEach((item) => {
      const userId = Number(item?.userId || item?.id || 0);
      if (!userId || uniqueOptions.has(String(userId))) {
        return;
      }

      uniqueOptions.set(String(userId), {
        id: userId,
        fullName: item?.fullName || '-',
      });
    });

    return Array.from(uniqueOptions.values()).sort((left, right) => (
      String(left.fullName || '').localeCompare(String(right.fullName || ''), 'de')
    ));
  }, [examDetail, referenceOptions.examinerOptions]);
  const selectedExam = examDetail?.exam || null;
  const isSelectedExamLocked = String(selectedExam?.status || '').trim() === 'closed';
  const canEditPracticalSheetsPermission = Boolean(examDetail?.permissions?.canEditPracticalSheets ?? canEditPracticalSheets);
  const canEditPracticalSheetsPermissionEffective = canEditPracticalSheetsPermission && !practicalEditForbidden;
  const canEditPracticalSheetsPanel = canEditPracticalSheetsPermissionEffective && !isSelectedExamLocked;
  const canCloseSelectedExam = Boolean(canManageAdministration && selectedExam && !isSelectedExamLocked);
  const canDeleteSelectedExam = Boolean(canManageAdministration && selectedExam && ['draft', 'active', 'closed'].includes(String(selectedExam.status || 'draft').trim()));
  const selectedPdfOption = useMemo(
    () => SRHT_EXAM_PDF_OPTIONS.find((item) => item.value === selectedPdfVariant) || SRHT_EXAM_PDF_OPTIONS[0],
    [selectedPdfVariant],
  );
  const selectedPdfCandidateNameById = useMemo(
    () => new Map((examDetail?.candidates || []).map((candidate) => [String(candidate.id), candidate.fullName || '-'])),
    [examDetail?.candidates],
  );

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0] || SRHT_EXAM_TAB_MANAGE);
    }
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    writePersistedTab(SRHT_EXAM_ACTIVE_TAB_STORAGE_KEY, activeTab, SRHT_EXAM_ALLOWED_TABS);
  }, [activeTab]);

  useEffect(() => {
    const validCandidateIds = new Set((examDetail?.candidates || []).map((candidate) => String(candidate.id)));
    const validUserIds = new Set((examDetail?.candidates || []).map((candidate) => String(candidate.userId)));

    setSelectedPdfCandidateIds((currentValue) => currentValue.filter((candidateId) => validCandidateIds.has(String(candidateId))));
    setSelectedAssessmentEventIdsByUserId((currentValue) => Object.fromEntries(
      Object.entries(currentValue || {}).filter(([userId]) => validUserIds.has(String(userId))),
    ));
  }, [examDetail?.candidates]);

  const persistDetailAsOfflinePackage = useCallback((detail) => {
    if (!SRHT_EXAM_OFFLINE_MODE_ENABLED) {
      return;
    }

    if (!detail?.exam?.id) {
      return;
    }

    writeOfflinePackage(detail.exam.id, {
      detail,
      cachedAt: new Date().toISOString(),
    });
    setOfflinePreparedExams(listPreparedOfflineExams());
  }, []);

  const applyExamDetail = useCallback((detail) => {
    setExamDetail(detail || null);
    setExamForm(buildExamForm(detail));
    setWrittenInputs(buildWrittenInputState(detail?.candidates || []));
    setPracticalEditForbidden(false);
  }, []);

  const loadExams = useCallback(async () => {
    if (!canAccessModule) {
      return;
    }

    try {
      const response = await getSrhtExams();
      const nextExams = response.data?.exams || [];
      setExamList(nextExams);
      writeExamListCache(nextExams);
      if (!selectedExamId && nextExams.length > 0) {
        setSelectedExamId(String(nextExams[0].id));
      }
    } catch (requestError) {
      const cachedExams = readExamListCache();
      if (cachedExams.length > 0) {
        setExamList(cachedExams);
        if (!selectedExamId && cachedExams.length > 0) {
          setSelectedExamId(String(cachedExams[0].id));
        }
        setNotice({ severity: 'warning', message: 'Keine Verbindung zum Server. Vorbereitete Prüfungen SRHT werden lokal angezeigt.' });
      } else {
        setError(requestError.response?.data?.error || 'Prüfungen SRHT konnten nicht geladen werden.');
      }
    }
  }, [canAccessModule, selectedExamId]);

  const loadReferenceOptions = useCallback(async () => {
    if (!canManageAdministration && !canEditPracticalSheets) {
      return;
    }

    try {
      const response = await getSrhtExamReferenceOptions();
      setReferenceOptions({
        courses: response.data?.courses || [],
        courseCandidates: response.data?.courseCandidates || [],
        chairOptions: response.data?.chairOptions || [],
        courseLeaderOptions: response.data?.courseLeaderOptions || [],
        examinerOptions: response.data?.examinerOptions || [],
        observerOptions: response.data?.observerOptions || [],
      });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Referenzdaten konnten nicht geladen werden.');
    }
  }, [canManageAdministration, canEditPracticalSheets]);

  const loadExamDetail = useCallback(async (examId) => {
    if (!examId) {
      applyExamDetail(null);
      return;
    }

    setDetailBusy(true);
    try {
      const response = await getSrhtExam(examId);
      const detail = response.data || null;
      applyExamDetail(detail);
      persistDetailAsOfflinePackage(detail);
      setError('');
    } catch (requestError) {
      if (!SRHT_EXAM_OFFLINE_MODE_ENABLED) {
        setError(requestError.response?.data?.error || 'Prüfungsdetails konnten nicht geladen werden.');
        return;
      }

      const offlinePackage = readOfflinePackage(examId);
      if (offlinePackage?.detail) {
        applyExamDetail(offlinePackage.detail);
        setNotice({ severity: 'warning', message: 'Prüfungsdaten werden aus dem Offline-Paket geladen.' });
      } else {
        setError(requestError.response?.data?.error || 'Prüfungsdetails konnten nicht geladen werden.');
      }
    } finally {
      setDetailBusy(false);
    }
  }, [applyExamDetail, persistDetailAsOfflinePackage]);

  useEffect(() => {
    setOfflinePreparedExams(listPreparedOfflineExams());

    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    loadExams();
    loadReferenceOptions();
  }, [loadExams, loadReferenceOptions]);

  useEffect(() => {
    if (selectedExamId) {
      loadExamDetail(selectedExamId);
    } else {
      applyExamDetail(null);
      setExamForm(buildExamForm(null));
    }
  }, [applyExamDetail, loadExamDetail, selectedExamId]);

  useEffect(() => {
    if (!canManageAdministration) {
      return;
    }

    const availableCandidateIds = new Set(
      referenceOptions.courseCandidates
        .filter((item) => examForm.sourceCourseIds.includes(Number(item.courseId)))
        .map((item) => Number(item.userId))
    );

    setExamForm((current) => ({
      ...current,
      candidateUserIds: current.candidateUserIds.filter((userId) => availableCandidateIds.has(Number(userId))),
    }));
  }, [canManageAdministration, examForm.sourceCourseIds, referenceOptions.courseCandidates]);

  const enqueueSyncItems = useCallback((items) => {
    setSyncQueueItems((current) => {
      const filteredCurrent = current.filter((queuedItem) => !items.some((nextItem) => (
        queuedItem.action === nextItem.action
        && String(queuedItem.examId) === String(nextItem.examId)
        && String(queuedItem.candidateId) === String(nextItem.candidateId)
      )));
      const nextQueue = [...filteredCurrent, ...items];
      writeSyncQueue(nextQueue);
      return nextQueue;
    });
  }, []);

  const updateLocalWrittenResult = useCallback((examId, candidateId, updater) => {
    let nextDetail = null;
    setExamDetail((current) => {
      if (!current) {
        return current;
      }

      const nextCandidates = (current.candidates || []).map((candidate) => {
        if (String(candidate.id) !== String(candidateId)) {
          return candidate;
        }

        const currentResult = candidate.writtenResult || null;
        return {
          ...candidate,
          writtenResult: updater(currentResult),
        };
      });

      nextDetail = {
        ...current,
        candidates: nextCandidates,
      };
      return nextDetail;
    });

    if (nextDetail?.exam?.id) {
      writeOfflinePackage(examId, {
        detail: nextDetail,
        cachedAt: new Date().toISOString(),
      });
      setOfflinePreparedExams(listPreparedOfflineExams());
    }
  }, []);

  const updateLocalPracticalSheetRecord = useCallback((stationCode, practicalSheet) => {
    if (!stationCode || !practicalSheet) {
      return;
    }

    setExamDetail((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        practicalSheets: {
          ...(current.practicalSheets || {}),
          [stationCode]: practicalSheet,
        },
      };
    });
  }, []);

  useEffect(() => {
    if (isOffline || syncBusy || syncQueueItems.length === 0) {
      return undefined;
    }

    let cancelled = false;

    const processQueue = async () => {
      setSyncBusy(true);
      for (const item of syncQueueItems) {
        if (cancelled) {
          return;
        }

        try {
          if (item.action === 'save') {
            await saveSrhtWrittenExamResult(item.examId, item.candidateId, { points: item.payload.points });
          } else if (item.action === 'finalize') {
            await finalizeSrhtWrittenExamResult(item.examId, item.candidateId);
          } else if (item.action === 'unlock') {
            await unlockSrhtWrittenExamResult(item.examId, item.candidateId, { reason: item.payload.reason });
          }

          const nextQueue = readSyncQueue().filter((queuedItem) => queuedItem.id !== item.id);
          writeSyncQueue(nextQueue);
          setSyncQueueItems(nextQueue);
        } catch (requestError) {
          const nextQueue = readSyncQueue().map((queuedItem) => (
            queuedItem.id === item.id
              ? { ...queuedItem, lastError: requestError.response?.data?.error || 'Synchronisierung fehlgeschlagen.' }
              : queuedItem
          ));
          writeSyncQueue(nextQueue);
          setSyncQueueItems(nextQueue);
          setNotice({ severity: 'warning', message: requestError.response?.data?.error || 'Mindestens eine Offline-Änderung konnte noch nicht synchronisiert werden.' });
          break;
        }
      }

      await loadExams();
      if (selectedExamId) {
        await loadExamDetail(selectedExamId);
      }
      setSyncBusy(false);
    };

    processQueue();

    return () => {
      cancelled = true;
    };
  }, [isOffline, loadExamDetail, loadExams, selectedExamId, syncBusy, syncQueueItems]);

  const filteredCandidateOptions = useMemo(
    () => referenceOptions.courseCandidates.filter((item) => examForm.sourceCourseIds.includes(Number(item.courseId))),
    [examForm.sourceCourseIds, referenceOptions.courseCandidates]
  );

  const currentPreparedOfflineExam = useMemo(
    () => offlinePreparedExams.find((item) => String(item.examId) === String(selectedExamId || '')) || null,
    [offlinePreparedExams, selectedExamId]
  );
  const practicalSheetRecords = useMemo(() => examDetail?.practicalSheets || {}, [examDetail?.practicalSheets]);

  const handleSelectExam = (examId) => {
    setSelectedExamId(String(examId || ''));
    setError('');
    setNotice(null);
    setPracticalEditForbidden(false);
  };

  const handleCreateNewExam = () => {
    setSelectedExamId('');
    setExamDetail(null);
    setExamForm(buildExamForm(null));
    setWrittenInputs({});
    setActiveTab(canManageAdministration ? SRHT_EXAM_TAB_MANAGE : SRHT_EXAM_TAB_REGULATION);
    setNotice({ severity: 'info', message: 'Neue Prüfung SRHT vorbereitet.' });
  };

  const handleCloseExam = async () => {
    if (!selectedExamId) {
      return;
    }

    setBusy(true);
    setError('');
    setNotice(null);
    try {
      const response = await closeSrhtExam(selectedExamId);
      const detail = response.data || null;
      applyExamDetail(detail);
      await loadExams();
      setCloseExamDialogOpen(false);
      setNotice({ severity: 'success', message: 'Prüfung SRHT abgeschlossen.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Prüfung SRHT konnte nicht abgeschlossen werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlockExam = async () => {
    if (!selectedExamId) {
      return;
    }

    setBusy(true);
    setError('');
    setNotice(null);
    try {
      const response = await unlockSrhtExam(selectedExamId, { reason: examUnlockReason });
      const detail = response.data || null;
      applyExamDetail(detail);
      await loadExams();
      setUnlockExamDialogOpen(false);
      setExamUnlockReason('');
      setNotice({ severity: 'success', message: 'Prüfung SRHT wieder geöffnet.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Prüfung SRHT konnte nicht wieder geöffnet werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteExam = async () => {
    if (!selectedExamId) {
      return;
    }

    const deletingExamId = String(selectedExamId);
    setBusy(true);
    setError('');
    setNotice(null);
    try {
      await deleteSrhtExam(deletingExamId);
      setDeleteExamDialogOpen(false);
      setSelectedExamId('');
      setExamDetail(null);
      setExamForm(buildExamForm(null));
      setWrittenInputs({});
      await loadExams();
      setNotice({ severity: 'success', message: 'Prüfung SRHT gelöscht.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Prüfung SRHT konnte nicht gelöscht werden.');
    } finally {
      setBusy(false);
    }
  };

  const loadPdfSourceData = async (targetCandidates, { includeAssessments = true } = {}) => {
    const courseIds = [...new Set((targetCandidates || [])
      .map((candidate) => Number(candidate.courseId || 0))
      .filter((courseId) => Number.isInteger(courseId) && courseId > 0))];
    const courseResponses = await Promise.all(courseIds.map((courseId) => getSrhtCourse(courseId)));
    const courseDetailsById = Object.fromEntries(
      courseResponses.map((response) => [String(response.data?.course?.id || ''), response.data || null]).filter((entry) => entry[0]),
    );

    const assessmentPackagesById = includeAssessments
      ? Object.fromEntries(
        (await Promise.all(
          [...new Set(courseResponses.flatMap((response) => (
            (response.data?.assessmentSources || []).map((item) => Number(item.assessment_event_id || 0))
          )).filter((eventId) => Number.isInteger(eventId) && eventId > 0))]
            .map((eventId) => getAssessmentOfflinePackage(eventId)),
        ))
          .map((response) => [String(response.data?.event?.id || ''), response.data || null])
          .filter((entry) => entry[0]),
      )
      : {};

    return {
      courseDetailsById,
      assessmentPackagesById,
    };
  };

  const executePdfExport = async ({
    targetCandidateIds,
    courseDetailsById,
    assessmentPackagesById,
    assessmentEventSelectionsByUserId = {},
  }) => {
    const candidateGroups = String(selectedPdfVariant || '').endsWith('-single')
      ? targetCandidateIds.map((candidateId) => [candidateId])
      : [targetCandidateIds];
    const brandingAssets = await loadLogbuchPdfBrandingAssets();
    let downloadCount = 0;

    for (const scopedCandidateIds of candidateGroups) {
      const model = buildSrhtExamExportModel({
        examDetail,
        courseDetailsById,
        assessmentPackagesById,
        variant: selectedPdfVariant,
        selectedCandidateIds: scopedCandidateIds,
        selectedAssessmentEventIdsByUserId: assessmentEventSelectionsByUserId,
      });

      if (!model.participants.length) {
        continue;
      }

      if (isFullChainPdfVariant(selectedPdfVariant)) {
        const response = await exportSrhtExamFullChainPdf(selectedExam.id, {
          variant: selectedPdfVariant,
          candidateIds: scopedCandidateIds,
          assessmentEventSelectionsByUserId,
        });
        downloadPdfResponse(response, buildSrhtExamPdfFilename(model));
      } else {
        const documentDefinition = buildSrhtExamPdfDocument({ model, brandingAssets });
        await pdfMake.createPdf(documentDefinition).download(buildSrhtExamPdfFilename(model));
      }

      downloadCount += 1;
    }

    return downloadCount;
  };

  const closeAssessmentSelectionDialog = () => {
    setAssessmentSelectionDialogState({
      open: false,
      candidates: [],
      selectionsByUserId: {},
      context: null,
    });
  };

  const handleConfirmAssessmentSelectionDialog = async () => {
    const pendingContext = assessmentSelectionDialogState.context;
    if (!pendingContext) {
      closeAssessmentSelectionDialog();
      return;
    }

    const missingSelection = assessmentSelectionDialogState.candidates.some((candidate) => {
      const selectedEventId = assessmentSelectionDialogState.selectionsByUserId[String(candidate.userId)] || '';
      return !String(selectedEventId).trim();
    });
    if (missingSelection) {
      setError('Bitte für jeden Teilnehmer genau ein Eignungsverfahren auswählen.');
      return;
    }

    setExportingPdf(true);
    setError('');
    setNotice(null);
    try {
      const normalizedSelectionsByUserId = Object.fromEntries(
        Object.entries(assessmentSelectionDialogState.selectionsByUserId).map(([userId, eventId]) => [String(userId), [Number(eventId)]]),
      );
      const mergedSelections = {
        ...selectedAssessmentEventIdsByUserId,
        ...normalizedSelectionsByUserId,
      };

      setSelectedAssessmentEventIdsByUserId(mergedSelections);
      const downloadCount = await executePdfExport({
        ...pendingContext,
        assessmentEventSelectionsByUserId: mergedSelections,
      });

      if (!downloadCount) {
        setError('Für die gewählte Exportvariante konnten keine Daten zusammengestellt werden.');
        return;
      }

      closeAssessmentSelectionDialog();
      setNotice({
        severity: 'success',
        message: downloadCount > 1 ? `${downloadCount} Prüfungs-PDFs wurden erstellt.` : 'Prüfungs-PDF wurde erstellt.',
      });
    } catch (requestError) {
      setError(await extractPdfErrorMessage(requestError, 'Prüfungs-PDF konnte nicht erstellt werden.'));
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedExam || !examDetail) {
      return;
    }
    const selectedCandidateIdSet = new Set(selectedPdfCandidateIds.map((candidateId) => String(candidateId)));
    if (selectedPdfOption.requiresParticipantSelection && selectedCandidateIdSet.size === 0) {
      setError('Bitte zuerst mindestens einen Teilnehmer für den PDF-Export auswählen.');
      return;
    }
    if (isOffline) {
      setError('Der PDF-Export für die Prüfung SRHT benötigt eine Serververbindung.');
      return;
    }

    setExportingPdf(true);
    setError('');
    setNotice(null);
    try {
      const targetCandidates = selectedPdfOption.requiresParticipantSelection
        ? (examDetail.candidates || []).filter((candidate) => selectedCandidateIdSet.has(String(candidate.id)))
        : (examDetail.candidates || []);
      if (!targetCandidates.length) {
        setError('Für die gewählte Exportvariante konnten keine Daten zusammengestellt werden.');
        return;
      }

      const needsLinkedSourceData = Boolean(selectedPdfOption.includesExam || selectedPdfOption.includesCourse || selectedPdfOption.includesAssessment);
      const { courseDetailsById, assessmentPackagesById } = needsLinkedSourceData
        ? await loadPdfSourceData(targetCandidates, { includeAssessments: selectedPdfOption.includesAssessment })
        : { courseDetailsById: {}, assessmentPackagesById: {} };
      const assessmentSelectionCandidates = selectedPdfOption.includesAssessment
        ? getSrhtExamAssessmentSelectionCandidates({
          candidates: targetCandidates,
          courseDetailsById,
          assessmentPackagesById,
          selectedAssessmentEventIdsByUserId,
        })
        : [];

      if (assessmentSelectionCandidates.length > 0) {
        setAssessmentSelectionDialogState({
          open: true,
          candidates: assessmentSelectionCandidates,
          selectionsByUserId: Object.fromEntries(assessmentSelectionCandidates.map((candidate) => {
            const selectedEventId = selectedAssessmentEventIdsByUserId[String(candidate.userId)]?.[0] || '';
            return [String(candidate.userId), selectedEventId ? String(selectedEventId) : ''];
          })),
          context: {
            targetCandidateIds: targetCandidates.map((candidate) => String(candidate.id)),
            courseDetailsById,
            assessmentPackagesById,
          },
        });
        return;
      }

      const downloadCount = await executePdfExport({
        targetCandidateIds: targetCandidates.map((candidate) => String(candidate.id)),
        courseDetailsById,
        assessmentPackagesById,
        assessmentEventSelectionsByUserId: selectedAssessmentEventIdsByUserId,
      });
      if (!downloadCount) {
        setError('Für die gewählte Exportvariante konnten keine Daten zusammengestellt werden.');
        return;
      }

      setNotice({
        severity: 'success',
        message: downloadCount > 1 ? `${downloadCount} Prüfungs-PDFs wurden erstellt.` : 'Prüfungs-PDF wurde erstellt.',
      });
    } catch (requestError) {
      setError(await extractPdfErrorMessage(requestError, 'Prüfungs-PDF konnte nicht erstellt werden.'));
    } finally {
      setExportingPdf(false);
    }
  };

  const handleChangeFormValue = (field) => (event) => {
    const nextValue = event.target.value;
    setExamForm((current) => {
      const normalizedValue = Array.isArray(nextValue) ? nextValue.map((value) => Number(value)) : nextValue;
      if (field === 'practicalExamDate') {
        return {
          ...current,
          practicalExamDate: normalizedValue,
          writtenExamDate: syncWrittenExamDateWithExamDate({
            previousExamDate: current.practicalExamDate,
            nextExamDate: normalizedValue,
            currentWrittenExamDate: current.writtenExamDate,
          }),
        };
      }

      return {
        ...current,
        [field]: normalizedValue,
      };
    });
  };

  const handleObserverNoteChange = (userId, value) => {
    setExamForm((current) => ({
      ...current,
      observerNotes: {
        ...current.observerNotes,
        [String(userId)]: value,
      },
    }));
  };

  const buildSavePayload = () => ({
    label: examForm.label,
    practicalExamDate: examForm.practicalExamDate,
    writtenExamDate: examForm.writtenExamDate,
    location: examForm.location,
    notes: examForm.notes,
    sourceCourseIds: examForm.sourceCourseIds,
    candidateUserIds: examForm.candidateUserIds,
    chairUserId: examForm.chairUserId || null,
    courseLeaderUserId: examForm.courseLeaderUserId || null,
    examinerUserIds: examForm.examinerUserIds,
    personalrat: buildStaffPayload(examForm.personalratUserIds),
    department37_4: buildStaffPayload(examForm.department37_4UserIds),
    equalOpportunityOfficers: buildStaffPayload(examForm.equalOpportunityOfficerUserIds),
    observers: buildStaffPayload(examForm.observerUserIds, examForm.observerNotes),
  });

  const handleSaveExam = async () => {
    setBusy(true);
    setError('');
    setNotice(null);
    try {
      const payload = buildSavePayload();
      const response = selectedExamId
        ? await updateSrhtExam(selectedExamId, payload)
        : await createSrhtExam(payload);

      const detail = response.data || null;
      applyExamDetail(detail);
      persistDetailAsOfflinePackage(detail);
      await loadExams();
      setSelectedExamId(String(detail?.exam?.id || ''));
      setNotice({ severity: 'success', message: selectedExamId ? 'Prüfung SRHT aktualisiert.' : 'Prüfung SRHT angelegt.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Prüfung SRHT konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleRefreshOfflinePackage = () => {
    if (!SRHT_EXAM_OFFLINE_MODE_ENABLED) {
      setError('Offlinepakete für Prüfung SRHT sind derzeit deaktiviert, solange die Zeitnahme serverseitig geführt wird.');
      return;
    }

    if (!examDetail?.exam?.id) {
      return;
    }

    persistDetailAsOfflinePackage(examDetail);
    setNotice({ severity: 'success', message: 'Offline-Paket für die Prüfung wurde aktualisiert.' });
  };

  const handleWrittenInputChange = (candidateId, value) => {
    setWrittenInputs((current) => ({
      ...current,
      [String(candidateId)]: value,
    }));
  };

  const handleSaveWrittenResult = async (candidate) => {
    const pointsValue = writtenInputs[String(candidate.id)] || '';
    const grade = getGradeFromPoints(pointsValue);
    if (grade === null) {
      setError('Für die schriftliche Prüfung sind ganzzahlige Punkte zwischen 0 und 129 erforderlich.');
      return;
    }

    setActionCandidateId(String(candidate.id));
    setError('');
    try {
      if (isOffline && !SRHT_EXAM_OFFLINE_MODE_ENABLED) {
        setError('Offlinebetrieb für Prüfung SRHT ist derzeit deaktiviert. Eine Serververbindung ist erforderlich.');
        return;
      }

      if (isOffline) {
        updateLocalWrittenResult(selectedExamId, candidate.id, (currentResult) => ({
          ...(currentResult || { id: `local-${candidate.id}` }),
          points: Number(pointsValue),
          grade,
          status: currentResult?.status || 'draft',
          updatedAt: new Date().toISOString(),
        }));
        enqueueSyncItems([createQueueItem('save', selectedExamId, candidate.id, { points: Number(pointsValue) })]);
        setNotice({ severity: 'info', message: 'Schriftliche Prüfung lokal gespeichert. Die Synchronisierung erfolgt automatisch.' });
        return;
      }

      await saveSrhtWrittenExamResult(selectedExamId, candidate.id, { points: Number(pointsValue) });
      await loadExamDetail(selectedExamId);
      setNotice({ severity: 'success', message: 'Schriftliche Prüfung gespeichert.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Schriftliche Prüfung konnte nicht gespeichert werden.');
    } finally {
      setActionCandidateId('');
    }
  };

  const handleFinalizeWrittenResult = async (candidate) => {
    const pointsValue = writtenInputs[String(candidate.id)] || '';
    const grade = getGradeFromPoints(pointsValue);
    if (grade === null) {
      setError('Vor dem Finalisieren müssen gültige Punkte zwischen 0 und 129 hinterlegt sein.');
      return;
    }

    setActionCandidateId(String(candidate.id));
    setError('');
    try {
      if (isOffline && !SRHT_EXAM_OFFLINE_MODE_ENABLED) {
        setError('Offlinebetrieb für Prüfung SRHT ist derzeit deaktiviert. Eine Serververbindung ist erforderlich.');
        return;
      }

      if (isOffline) {
        updateLocalWrittenResult(selectedExamId, candidate.id, (currentResult) => ({
          ...(currentResult || { id: `local-${candidate.id}` }),
          points: Number(pointsValue),
          grade,
          status: 'finalized',
          finalizedAt: new Date().toISOString(),
        }));
        enqueueSyncItems([
          createQueueItem('save', selectedExamId, candidate.id, { points: Number(pointsValue) }),
          createQueueItem('finalize', selectedExamId, candidate.id),
        ]);
        setNotice({ severity: 'info', message: 'Finalisierung lokal vorgemerkt. Die Synchronisierung erfolgt automatisch.' });
        return;
      }

      await saveSrhtWrittenExamResult(selectedExamId, candidate.id, { points: Number(pointsValue) });
      await finalizeSrhtWrittenExamResult(selectedExamId, candidate.id);
      await loadExamDetail(selectedExamId);
      setNotice({ severity: 'success', message: 'Schriftliche Prüfung finalisiert.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Schriftliche Prüfung konnte nicht finalisiert werden.');
    } finally {
      setActionCandidateId('');
    }
  };

  const handleConfirmUnlock = async () => {
    if (!unlockDialog.candidateId) {
      return;
    }

    setActionCandidateId(String(unlockDialog.candidateId));
    setError('');
    try {
      if (isOffline && !SRHT_EXAM_OFFLINE_MODE_ENABLED) {
        setError('Offlinebetrieb für Prüfung SRHT ist derzeit deaktiviert. Eine Serververbindung ist erforderlich.');
        return;
      }

      if (isOffline) {
        updateLocalWrittenResult(selectedExamId, unlockDialog.candidateId, (currentResult) => ({
          ...(currentResult || { id: `local-${unlockDialog.candidateId}` }),
          status: 'draft',
          lastUnlockedAt: new Date().toISOString(),
        }));
        enqueueSyncItems([
          createQueueItem('unlock', selectedExamId, unlockDialog.candidateId, { reason: unlockDialog.reason }),
        ]);
        setNotice({ severity: 'info', message: 'Entsperrung lokal vorgemerkt. Die Synchronisierung erfolgt automatisch.' });
      } else {
        await unlockSrhtWrittenExamResult(selectedExamId, unlockDialog.candidateId, { reason: unlockDialog.reason });
        await loadExamDetail(selectedExamId);
        setNotice({ severity: 'success', message: 'Schriftliche Prüfung entsperrt.' });
      }
      setUnlockDialog({ open: false, candidateId: '', reason: '' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Schriftliche Prüfung konnte nicht entsperrt werden.');
    } finally {
      setActionCandidateId('');
    }
  };

  const getPracticalSheetExpectedUpdatedAt = useCallback((stationCode, fallbackValue = '') => (
    fallbackValue || practicalSheetRecords?.[stationCode]?.updatedAt || ''
  ), [practicalSheetRecords]);

  const handleStalePracticalSheetConflict = useCallback(async (stationCode, requestError, fallbackMessage) => {
    const staleSheet = requestError?.response?.data?.practicalSheet || null;
    if (staleSheet && stationCode) {
      updateLocalPracticalSheetRecord(stationCode, staleSheet);
    }
    if (selectedExamId) {
      await loadExamDetail(selectedExamId);
    }
    setError(requestError?.response?.data?.error || fallbackMessage);
  }, [loadExamDetail, selectedExamId, updateLocalPracticalSheetRecord]);

  const handleSyncPracticalTimerSession = async (stationCode, rawPayload, timerSession, options = {}) => {
    if (!selectedExamId || !stationCode) {
      return;
    }

    if (isOffline) {
      setError('Offlinebetrieb für Prüfung SRHT ist derzeit deaktiviert. Laufende Zeitnahmen benötigen eine Serververbindung.');
      throw new Error('srht_offline_disabled');
    }

    try {
      const response = await updateSrhtPracticalTimerSession(selectedExamId, stationCode, {
        rawPayload,
        timerSession,
        expectedUpdatedAt: getPracticalSheetExpectedUpdatedAt(stationCode, options.expectedUpdatedAt),
      });
      setError('');
      updateLocalPracticalSheetRecord(stationCode, response.data?.practicalSheet || null);
    } catch (requestError) {
      if (requestError?.response?.status === 403) {
        setPracticalEditForbidden(true);
      }
      if (requestError?.response?.status === 409 && requestError?.response?.data?.errorCode === 'stale_practical_sheet') {
        await handleStalePracticalSheetConflict(stationCode, requestError, 'Die laufende Zeitnahme konnte wegen paralleler Änderungen nicht gespeichert werden.');
      } else {
        setError(requestError.response?.data?.error || 'Die laufende Zeitnahme konnte nicht aktualisiert werden.');
      }
      throw requestError;
    }
  };

  const handleSavePracticalSheet = async (stationCode, rawPayload, options = {}) => {
    if (!selectedExamId || !stationCode) {
      return;
    }

    const silent = options?.silent === true;

    if (isOffline && !SRHT_EXAM_OFFLINE_MODE_ENABLED) {
      setError('Offlinebetrieb für Prüfung SRHT ist derzeit deaktiviert. Eine Serververbindung ist erforderlich.');
      return;
    }

    if (!silent) {
      setPracticalSheetAction('save');
    }
    setError('');
    try {
      const response = await saveSrhtPracticalSheet(selectedExamId, stationCode, {
        rawPayload,
        expectedUpdatedAt: getPracticalSheetExpectedUpdatedAt(stationCode),
      });
      updateLocalPracticalSheetRecord(stationCode, response.data?.practicalSheet || null);
      if (!silent) {
        setNotice({ severity: 'success', message: 'Praktischer Prüfungsbogen gespeichert.' });
      }
    } catch (requestError) {
      if (requestError?.response?.status === 403) {
        setPracticalEditForbidden(true);
      }
      if (requestError?.response?.status === 409 && requestError?.response?.data?.errorCode === 'stale_practical_sheet') {
        await handleStalePracticalSheetConflict(stationCode, requestError, 'Praktischer Prüfungsbogen konnte wegen paralleler Änderungen nicht gespeichert werden.');
      } else {
        setError(requestError.response?.data?.error || 'Praktischer Prüfungsbogen konnte nicht gespeichert werden.');
      }
    } finally {
      if (!silent) {
        setPracticalSheetAction('');
      }
    }
  };

  const handleFinalizePracticalSheet = async (stationCode, rawPayload, candidateId, options = {}) => {
    if (!selectedExamId || !stationCode) {
      return;
    }

    if (isOffline && !SRHT_EXAM_OFFLINE_MODE_ENABLED) {
      setError('Offlinebetrieb für Prüfung SRHT ist derzeit deaktiviert. Eine Serververbindung ist erforderlich.');
      return;
    }

    setPracticalSheetAction('finalize');
    setError('');
    try {
      const saveResponse = await saveSrhtPracticalSheet(selectedExamId, stationCode, {
        rawPayload,
        expectedUpdatedAt: getPracticalSheetExpectedUpdatedAt(stationCode),
      });
      const nextExpectedUpdatedAt = saveResponse?.data?.practicalSheet?.updatedAt || '';
      await finalizeSrhtPracticalSheet(selectedExamId, stationCode, {
        candidateId,
        expectedUpdatedAt: getPracticalSheetExpectedUpdatedAt(stationCode, nextExpectedUpdatedAt),
        ...options,
      });
      await loadExamDetail(selectedExamId);
      setNotice({ severity: 'success', message: 'Praktischer Prüfungsbogen finalisiert.' });
    } catch (requestError) {
      if (requestError?.response?.status === 403) {
        setPracticalEditForbidden(true);
      }
      if (requestError?.response?.status === 409 && requestError?.response?.data?.errorCode === 'stale_practical_sheet') {
        await handleStalePracticalSheetConflict(stationCode, requestError, 'Praktischer Prüfungsbogen konnte wegen paralleler Änderungen nicht finalisiert werden.');
      } else {
        setError(requestError.response?.data?.error || 'Praktischer Prüfungsbogen konnte nicht finalisiert werden.');
      }
    } finally {
      setPracticalSheetAction('');
    }
  };

  const handleUnlockPracticalSheet = async (stationCode, reason, candidateId, options = {}) => {
    if (!selectedExamId || !stationCode) {
      return;
    }

    if (isOffline && !SRHT_EXAM_OFFLINE_MODE_ENABLED) {
      setError('Offlinebetrieb für Prüfung SRHT ist derzeit deaktiviert. Eine Serververbindung ist erforderlich.');
      return;
    }

    setPracticalSheetAction('unlock');
    setError('');
    try {
      await unlockSrhtPracticalSheet(selectedExamId, stationCode, {
        reason,
        candidateId,
        expectedUpdatedAt: getPracticalSheetExpectedUpdatedAt(stationCode),
        ...options,
      });
      await loadExamDetail(selectedExamId);
      setNotice({ severity: 'success', message: 'Praktischer Prüfungsbogen entsperrt.' });
    } catch (requestError) {
      if (requestError?.response?.status === 403) {
        setPracticalEditForbidden(true);
      }
      if (requestError?.response?.status === 409 && requestError?.response?.data?.errorCode === 'stale_practical_sheet') {
        await handleStalePracticalSheetConflict(stationCode, requestError, 'Praktischer Prüfungsbogen konnte wegen paralleler Änderungen nicht entsperrt werden.');
      } else {
        setError(requestError.response?.data?.error || 'Praktischer Prüfungsbogen konnte nicht entsperrt werden.');
      }
    } finally {
      setPracticalSheetAction('');
    }
  };

  const handleSaveOralRetake = async (candidateId, payload) => {
    if (!selectedExamId || !candidateId) {
      return;
    }

    setFinalEvaluationActionCandidateId(String(candidateId));
    setError('');
    try {
      await saveSrhtOralRetake(selectedExamId, candidateId, payload);
      await loadExamDetail(selectedExamId);
      setNotice({ severity: 'success', message: 'Mündliche Nachprüfung gespeichert.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Die mündliche Nachprüfung konnte nicht gespeichert werden.');
      throw requestError;
    } finally {
      setFinalEvaluationActionCandidateId('');
    }
  };

  const handleTogglePersonalFinalResultsRelease = async () => {
    if (!selectedExamId) {
      return;
    }

    setFinalEvaluationGlobalBusy(true);
    setError('');
    try {
      await setSrhtPersonalFinalResultsRelease(selectedExamId, !(examDetail?.exam?.personalResultsReleased));
      await loadExamDetail(selectedExamId);
      setNotice({ severity: 'success', message: examDetail?.exam?.personalResultsReleased ? 'Freigabe persönlicher Ergebnisse zurückgenommen.' : 'Persönliche Ergebnisse freigegeben.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Die Freigabe persönlicher Ergebnisse konnte nicht gespeichert werden.');
      throw requestError;
    } finally {
      setFinalEvaluationGlobalBusy(false);
    }
  };

  const handleToggleAllFinalResultsRelease = async () => {
    if (!selectedExamId) {
      return;
    }

    setFinalEvaluationGlobalBusy(true);
    setError('');
    try {
      await setSrhtAllFinalResultsRelease(selectedExamId, !(examDetail?.exam?.allResultsReleased || examDetail?.exam?.all_results_released));
      await loadExamDetail(selectedExamId);
      setNotice({ severity: 'success', message: (examDetail?.exam?.allResultsReleased || examDetail?.exam?.all_results_released) ? 'Freigabe aller Ergebnisse zurückgenommen.' : 'Alle Ergebnisse freigegeben.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Die Gesamtergebnisse konnten nicht freigegeben werden.');
      throw requestError;
    } finally {
      setFinalEvaluationGlobalBusy(false);
    }
  };

  if (!canAccessModule) {
    return <Alert severity="warning">Für die Prüfung SRHT ist mindestens eine Lehrhöhenretter-, Bewerber- oder Beobachter-Rolle erforderlich.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Prüfung SRHT
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          {SRHT_EXAM_OFFLINE_MODE_ENABLED && syncBusy ? <Chip color="info" label="Synchronisiert Offline-Änderungen" /> : null}
          {SRHT_EXAM_OFFLINE_MODE_ENABLED && syncQueueItems.length > 0 ? <Chip color="warning" label={`${syncQueueItems.length} lokale Änderungen offen`} /> : null}
          {SRHT_EXAM_OFFLINE_MODE_ENABLED && currentPreparedOfflineExam ? <Chip color="success" label={`Offline-Paket: ${currentPreparedOfflineExam.candidateCount} Bewerber`} /> : null}
        </Stack>
      </Stack>

      {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
      {notice ? <Alert severity={notice.severity || 'info'} onClose={() => setNotice(null)}>{notice.message}</Alert> : null}
      {isOffline ? (
        <Alert severity="warning">
          {SRHT_EXAM_OFFLINE_MODE_ENABLED
            ? 'Offline-Modus aktiv. Schriftliche Ergebnisse werden lokal zwischengespeichert und später synchronisiert.'
            : 'Offlinebetrieb für Prüfung SRHT ist derzeit deaktiviert. Für schriftliche Ergebnisse, Prüfbögen und laufende Zeitnahmen ist eine Serververbindung erforderlich.'}
        </Alert>
      ) : null}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CollapsibleModuleHeaderCard
            storageKey={SRHT_EXAM_HEADER_COLLAPSE_STORAGE_KEY}
            sx={{ mb: 2 }}
            summary={(
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 280 } }} disabled={examList.length === 0}>
                  <InputLabel id="srht-exam-select-label">Prüfung</InputLabel>
                  <Select
                    labelId="srht-exam-select-label"
                    label="Prüfung"
                    value={selectedExamId}
                    onChange={(event) => handleSelectExam(event.target.value)}
                  >
                    {examList.map((exam) => (
                      <MenuItem key={exam.id} value={String(exam.id)}>{exam.label || 'Prüfung SRHT'}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {canManageAdministration ? (
                  <Button variant="text" onClick={handleCreateNewExam} sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}>
                    Neue Prüfung vorbereiten
                  </Button>
                ) : null}
              </Stack>
            )}
          >
            {selectedExam ? (
              <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`Schriftlich: ${selectedExam.written_exam_date || '-'}`} />
                  <Chip label={`Praktisch: ${selectedExam.practical_exam_date || selectedExam.exam_date || '-'}`} />
                  <Chip label={`Lehrgänge: ${examDetail?.sourceCourses?.length || 0}`} color="primary" />
                  <Chip label={`Teilnehmer: ${examDetail?.candidates?.length || 0}`} color="secondary" />
                  <Chip label={getExamStatusLabel(selectedExam.status)} color={getExamStatusChipColor(selectedExam.status)} />
                </Stack>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  {canViewWrittenExam ? (
                    <Stack spacing={0.75}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                      <TextField
                        select
                        size="small"
                        label="PDF-Inhalt"
                        value={selectedPdfVariant}
                        onChange={(event) => setSelectedPdfVariant(String(event.target.value))}
                        sx={{ minWidth: { xs: '100%', sm: 320 } }}
                      >
                        {SRHT_EXAM_PDF_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                      {selectedPdfOption.requiresParticipantSelection ? (
                        <TextField
                          select
                          size="small"
                          label="Teilnehmer"
                          value={selectedPdfCandidateIds}
                          onChange={(event) => setSelectedPdfCandidateIds(normalizeMultiSelectValue(event.target.value).map((value) => String(value)))}
                          sx={{ minWidth: { xs: '100%', sm: 320 } }}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return selected
                                .map((value) => selectedPdfCandidateNameById.get(String(value)) || value)
                                .join(', ');
                            },
                          }}
                        >
                          {(examDetail?.candidates || []).map((candidate) => (
                            <MenuItem key={candidate.id} value={String(candidate.id)}>
                              <Checkbox checked={selectedPdfCandidateIds.includes(String(candidate.id))} />
                              {candidate.fullName || '-'}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : null}
                      <Button
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleExportPdf}
                        disabled={busy || exportingPdf || !selectedExamId}
                      >
                        {exportingPdf ? 'Erstellt PDF...' : 'PDF exportieren'}
                      </Button>
                      </Stack>
                      {String(selectedPdfVariant || '').endsWith('-single') ? (
                        <Typography variant="caption" color="text.secondary">
                          Mehrere ausgewählte Teilnehmer werden als einzelne PDFs nacheinander heruntergeladen.
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : null}
                  {canManageAdministration ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                      {isSelectedExamLocked ? (
                        <Button
                          variant="outlined"
                          color="warning"
                          startIcon={<LockOpenIcon />}
                          onClick={() => setUnlockExamDialogOpen(true)}
                          disabled={busy}
                        >
                          Wieder öffnen
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          color="success"
                          startIcon={<VerifiedIcon />}
                          onClick={() => setCloseExamDialogOpen(true)}
                          disabled={busy || !canCloseSelectedExam}
                        >
                          Prüfung abschließen
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => setDeleteExamDialogOpen(true)}
                        disabled={busy || !canDeleteSelectedExam}
                      >
                        Prüfung löschen
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              </Stack>
            ) : (
              <Alert severity="info">
                {examList.length === 0 ? 'Noch keine Prüfungen vorhanden.' : 'Noch keine Prüfung ausgewählt.'}
              </Alert>
            )}
          </CollapsibleModuleHeaderCard>

          <Paper variant="outlined">
            <Tabs
              value={activeTab}
              onChange={(_, nextValue) => setActiveTab(nextValue)}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              {visibleTabs.includes(SRHT_EXAM_TAB_MANAGE) ? <Tab value={SRHT_EXAM_TAB_MANAGE} label="Verwaltung" /> : null}
              {visibleTabs.includes(SRHT_EXAM_TAB_REGULATION) ? <Tab value={SRHT_EXAM_TAB_REGULATION} label="Ausbildungs- & Prüfungsordnung" /> : null}
              {visibleTabs.includes(SRHT_EXAM_TAB_WRITTEN) ? <Tab value={SRHT_EXAM_TAB_WRITTEN} label="Schriftliche Prüfung" /> : null}
              {visibleTabs.includes(SRHT_EXAM_TAB_SHEETS) ? <Tab value={SRHT_EXAM_TAB_SHEETS} label="Prüfbögen" /> : null}
              {visibleTabs.includes(SRHT_EXAM_TAB_FINAL) ? <Tab value={SRHT_EXAM_TAB_FINAL} label="Abschlussauswertung" /> : null}
            </Tabs>
            <Divider />
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              {detailBusy ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </Stack>
              ) : null}

              {!detailBusy && activeTab === SRHT_EXAM_TAB_MANAGE ? (
                <Stack spacing={2.5}>
                  {!canManageAdministration ? <Alert severity="info">Stammdaten und Personal können nur durch Lehrhöhenretter Bereich Ausbildung oder höher geändert werden.</Alert> : null}

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField label="Prüfungsbezeichnung" value={examForm.label} onChange={handleChangeFormValue('label')} fullWidth disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField label="Datum schriftliche Prüfung" type="date" value={examForm.writtenExamDate} onChange={handleChangeFormValue('writtenExamDate')} fullWidth disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField label="Datum praktische Prüfung" type="date" value={examForm.practicalExamDate} onChange={handleChangeFormValue('practicalExamDate')} fullWidth disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Ort" value={examForm.location} onChange={handleChangeFormValue('location')} fullWidth disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Notizen" value={examForm.notes} onChange={handleChangeFormValue('notes')} fullWidth multiline minRows={3} disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked} />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                          Lehrgang SRHT als Quelle
                        </Typography>
                        <TextField
                          value={examForm.sourceCourseIds}
                          onChange={handleChangeFormValue('sourceCourseIds')}
                          fullWidth
                          select
                          disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return referenceOptions.courses
                                .filter((course) => selected.includes(Number(course.id)))
                                .map((course) => course.label)
                                .join(', ');
                            },
                          }}
                          inputProps={{ 'aria-label': 'Lehrgang SRHT als Quelle' }}
                        >
                          {referenceOptions.courses.map((course) => (
                            <MenuItem key={course.id} value={course.id}>
                              {course.label} ({course.candidateCount} Bewerber)
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                          Bewerber aus Lehrgang SRHT
                        </Typography>
                        <TextField
                          value={examForm.candidateUserIds}
                          onChange={handleChangeFormValue('candidateUserIds')}
                          fullWidth
                          select
                          disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return filteredCandidateOptions
                                .filter((candidate) => selected.includes(Number(candidate.userId)))
                                .map((candidate) => candidate.fullName)
                                .join(', ');
                            },
                          }}
                          inputProps={{ 'aria-label': 'Bewerber aus Lehrgang SRHT' }}
                        >
                          {filteredCandidateOptions.map((candidate) => (
                            <MenuItem key={`${candidate.courseId}-${candidate.userId}`} value={candidate.userId}>
                              {candidate.fullName} · {candidate.courseLabel}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Typography variant="caption" color="text.secondary">
                          Teilnehmer mit dem Status „Zu viel Fehlzeiten“ werden serverseitig bereits aus dieser Auswahl ausgeschlossen.
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                          Prüfungsvorsitz
                        </Typography>
                        <TextField
                          value={examForm.chairUserId}
                          onChange={handleChangeFormValue('chairUserId')}
                          fullWidth
                          select
                          disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked}
                          SelectProps={{ displayEmpty: true }}
                          inputProps={{ 'aria-label': 'Prüfungsvorsitz' }}
                        >
                          <MenuItem value="">Auswählen</MenuItem>
                          {referenceOptions.chairOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>{option.fullName}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                          Lehrgangsleiter
                        </Typography>
                        <TextField
                          value={examForm.courseLeaderUserId}
                          onChange={handleChangeFormValue('courseLeaderUserId')}
                          fullWidth
                          select
                          disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked}
                          SelectProps={{ displayEmpty: true }}
                          inputProps={{ 'aria-label': 'Lehrgangsleiter' }}
                        >
                          <MenuItem value="">Auswählen</MenuItem>
                          {referenceOptions.courseLeaderOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>{option.fullName}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                          Prüfer
                        </Typography>
                        <TextField
                          value={examForm.examinerUserIds}
                          onChange={handleChangeFormValue('examinerUserIds')}
                          fullWidth
                          select
                          disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return referenceOptions.examinerOptions
                                .filter((item) => selected.includes(Number(item.id)))
                                .map((item) => item.fullName)
                                .join(', ');
                            },
                          }}
                          inputProps={{ 'aria-label': 'Prüfer' }}
                        >
                          {referenceOptions.examinerOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>{option.fullName}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                          Personalrat (optional)
                        </Typography>
                        <TextField
                          value={examForm.personalratUserIds}
                          onChange={handleChangeFormValue('personalratUserIds')}
                          fullWidth
                          select
                          disabled={!canManageAdministration || busy || isOffline}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return referenceOptions.observerOptions
                                .filter((item) => selected.includes(Number(item.id)))
                                .map((item) => item.fullName)
                                .join(', ');
                            },
                          }}
                          inputProps={{ 'aria-label': 'Personalrat' }}
                        >
                          {referenceOptions.observerOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>{option.fullName}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                          Abteilung 37/4 (optional)
                        </Typography>
                        <TextField
                          value={examForm.department37_4UserIds}
                          onChange={handleChangeFormValue('department37_4UserIds')}
                          fullWidth
                          select
                          disabled={!canManageAdministration || busy || isOffline}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return referenceOptions.observerOptions
                                .filter((item) => selected.includes(Number(item.id)))
                                .map((item) => item.fullName)
                                .join(', ');
                            },
                          }}
                          inputProps={{ 'aria-label': 'Abteilung 37/4' }}
                        >
                          {referenceOptions.observerOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>{option.fullName}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                          Gleichstellungsvertretung (optional)
                        </Typography>
                        <TextField
                          value={examForm.equalOpportunityOfficerUserIds}
                          onChange={handleChangeFormValue('equalOpportunityOfficerUserIds')}
                          fullWidth
                          select
                          disabled={!canManageAdministration || busy || isOffline}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return referenceOptions.observerOptions
                                .filter((item) => selected.includes(Number(item.id)))
                                .map((item) => item.fullName)
                                .join(', ');
                            },
                          }}
                          inputProps={{ 'aria-label': 'Gleichstellungsvertretung' }}
                        >
                          {referenceOptions.observerOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>{option.fullName}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                          Beobachter (optional)
                        </Typography>
                        <TextField
                          value={examForm.observerUserIds}
                          onChange={handleChangeFormValue('observerUserIds')}
                          fullWidth
                          select
                          disabled={!canManageAdministration || busy || isOffline}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return referenceOptions.observerOptions
                                .filter((item) => selected.includes(Number(item.id)))
                                .map((item) => item.fullName)
                                .join(', ');
                            },
                          }}
                          inputProps={{ 'aria-label': 'Beobachter' }}
                        >
                          {referenceOptions.observerOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>{option.fullName}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </Grid>
                  </Grid>

                  {examForm.observerUserIds.length > 0 ? (
                    <Grid container spacing={2}>
                      {examForm.observerUserIds.map((observerUserId) => {
                        const observer = referenceOptions.observerOptions.find((item) => Number(item.id) === Number(observerUserId));
                        return (
                          <Grid item xs={12} md={6} key={observerUserId}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                                {`Hinweis Beobachter${observer ? `: ${observer.fullName}` : ''}`}
                              </Typography>
                              <TextField
                                value={examForm.observerNotes[String(observerUserId)] || ''}
                                onChange={(event) => handleObserverNoteChange(observerUserId, event.target.value)}
                                fullWidth
                                multiline
                                minRows={2}
                                disabled={!canManageAdministration || busy || isOffline || isSelectedExamLocked}
                                placeholder="Hinweis eintragen"
                                inputProps={{ 'aria-label': `Hinweis Beobachter${observer ? `: ${observer.fullName}` : ''}` }}
                              />
                            </Stack>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : null}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    {canManageAdministration ? (
                      <Button variant="contained" onClick={handleSaveExam} disabled={busy || isOffline || isSelectedExamLocked}>
                        {selectedExamId ? 'Stammdaten speichern' : 'Prüfung anlegen'}
                      </Button>
                    ) : null}
                    {SRHT_EXAM_OFFLINE_MODE_ENABLED && examDetail?.exam?.id ? (
                      <Button variant="outlined" onClick={handleRefreshOfflinePackage} disabled={busy}>
                        Offline-Paket aktualisieren
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              ) : null}

              {!detailBusy && activeTab === SRHT_EXAM_TAB_REGULATION ? <SrhtExamRegulationPanel /> : null}

              {!detailBusy && activeTab === SRHT_EXAM_TAB_WRITTEN ? (
                <Stack spacing={2}>
                  {!selectedExamId ? <Alert severity="info">Bitte zuerst eine Prüfung SRHT auswählen oder anlegen.</Alert> : null}
                  {!canViewWrittenExam ? <Alert severity="warning">Die schriftliche Prüfung ist nur für Lehrhöhenretter und höher sichtbar.</Alert> : null}
                  {selectedExamId && canViewWrittenExam && examForm.practicalExamDate ? (
                    <Card variant="outlined">
                      <CardContent>
                        <Stack spacing={0.75}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Prüfungsdaten
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Schriftliche Prüfung: {examForm.writtenExamDate || getDefaultWrittenExamDate(examForm.practicalExamDate) || 'kein Datum hinterlegt'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Praktische Prüfung: {examForm.practicalExamDate}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : null}
                  {selectedExamId && canViewWrittenExam && (examDetail?.candidates || []).length === 0 ? (
                    <Alert severity="info">Für diese Prüfung wurden noch keine Bewerber übernommen.</Alert>
                  ) : null}
                  {selectedExamId && canViewWrittenExam ? (examDetail?.candidates || []).map((candidate) => {
                    const currentPoints = writtenInputs[String(candidate.id)] ?? '';
                    const currentGrade = getGradeFromPoints(currentPoints);
                    const isFinalized = candidate.writtenResult?.status === 'finalized';
                    const isBusyForCandidate = String(actionCandidateId) === String(candidate.id);

                    return (
                      <Card key={candidate.id} variant="outlined">
                        <CardContent>
                          <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                              <Box>
                                <Typography variant="h6">{candidate.fullName}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {candidate.courseLabel || 'Kein Lehrgang'} · {candidate.candidateRoleVariant === 'bewerber_srht_extern' ? 'Bewerber extern' : 'Bewerber'}
                                </Typography>
                              </Box>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Chip label={isFinalized ? 'Finalisiert' : 'Entwurf'} color={isFinalized ? 'success' : 'default'} />
                                <Chip label={currentGrade ? `Note ${currentGrade}` : 'Noch keine Punkte'} color={getGradeChipColor(currentGrade)} />
                              </Stack>
                            </Stack>

                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                  label="Punkte (0-129)"
                                  value={currentPoints}
                                  onChange={(event) => handleWrittenInputChange(candidate.id, event.target.value)}
                                  fullWidth
                                  disabled={!canEditWrittenExam || isBusyForCandidate || (isOffline && (!SRHT_EXAM_OFFLINE_MODE_ENABLED || !currentPreparedOfflineExam))}
                                />
                              </Grid>
                              <Grid item xs={12} sm={8} md={9}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                  {canEditWrittenExam ? (
                                    <Button variant="contained" onClick={() => handleSaveWrittenResult(candidate)} disabled={isBusyForCandidate || (isOffline && (!SRHT_EXAM_OFFLINE_MODE_ENABLED || !currentPreparedOfflineExam))}>
                                      Speichern
                                    </Button>
                                  ) : null}
                                  {canEditWrittenExam ? (
                                    <Button variant="outlined" onClick={() => handleFinalizeWrittenResult(candidate)} disabled={isBusyForCandidate || (isOffline && (!SRHT_EXAM_OFFLINE_MODE_ENABLED || !currentPreparedOfflineExam)) || isFinalized}>
                                      Finalisieren
                                    </Button>
                                  ) : null}
                                  {canEditWrittenExam ? (
                                    <Button
                                      variant="text"
                                      color="warning"
                                      onClick={() => setUnlockDialog({ open: true, candidateId: String(candidate.id), reason: '' })}
                                      disabled={isBusyForCandidate || !candidate.writtenResult || !isFinalized || (isOffline && (!SRHT_EXAM_OFFLINE_MODE_ENABLED || !currentPreparedOfflineExam))}
                                    >
                                      Entsperren
                                    </Button>
                                  ) : null}
                                </Stack>
                              </Grid>
                            </Grid>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  }) : null}
                </Stack>
              ) : null}

              {activeTab === SRHT_EXAM_TAB_SHEETS ? (
                <SrhtExamPracticalSheetsPanel
                  examId={selectedExamId}
                  examLabel={examDetail?.exam?.label || examForm.label}
                  candidates={examDetail?.candidates || []}
                  examiners={practicalExaminerOptions}
                  timeControllerOptions={practicalTimeControllerOptions}
                  sheetRecords={practicalSheetRecords}
                  canEdit={canEditPracticalSheetsPanel}
                  canEditByPermission={canEditPracticalSheetsPermissionEffective}
                  isExamLocked={isSelectedExamLocked}
                  isOffline={isOffline}
                  actionBusy={practicalSheetAction}
                  onSyncTimerSession={handleSyncPracticalTimerSession}
                  onSave={handleSavePracticalSheet}
                  onFinalize={handleFinalizePracticalSheet}
                  onUnlock={handleUnlockPracticalSheet}
                />
              ) : null}
              {!detailBusy && activeTab === SRHT_EXAM_TAB_FINAL ? (
                <SrhtExamFinalEvaluationPanel
                  examDetail={examDetail}
                  canEdit={Boolean((examDetail?.permissions?.canEditFinalEvaluation ?? canManageAdministration) && !isSelectedExamLocked)}
                  isLocked={isSelectedExamLocked}
                  actionCandidateId={finalEvaluationActionCandidateId}
                  allResultsReleaseBusy={finalEvaluationGlobalBusy}
                  onSaveOralRetake={handleSaveOralRetake}
                  onTogglePersonalResultsRelease={handleTogglePersonalFinalResultsRelease}
                  onToggleAllResultsRelease={handleToggleAllFinalResultsRelease}
                />
              ) : null}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={unlockDialog.open} onClose={() => setUnlockDialog({ open: false, candidateId: '', reason: '' })} fullWidth maxWidth="sm">
        <DialogTitle>Schriftliche Prüfung entsperren</DialogTitle>
        <DialogContent>
          <TextField
            label="Begründung"
            value={unlockDialog.reason}
            onChange={(event) => setUnlockDialog((current) => ({ ...current, reason: event.target.value }))}
            fullWidth
            multiline
            minRows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialog({ open: false, candidateId: '', reason: '' })}>Abbrechen</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmUnlock} disabled={!unlockDialog.reason.trim()}>
            Entsperren
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assessmentSelectionDialogState.open}
        onClose={() => {
          if (!exportingPdf) {
            closeAssessmentSelectionDialog();
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Eignungsverfahren auswählen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Für die ausgewählten Teilnehmer wurden mehrere Eignungsverfahren gefunden. Bitte pro Teilnehmer festlegen, welches Verfahren im kombinierten PDF verwendet werden soll.
            </Typography>
            {assessmentSelectionDialogState.candidates.map((candidate) => (
              <TextField
                key={`assessment-selection-${candidate.userId}`}
                select
                fullWidth
                size="small"
                label={candidate.fullName || 'Teilnehmer'}
                value={assessmentSelectionDialogState.selectionsByUserId[String(candidate.userId)] || ''}
                onChange={(event) => setAssessmentSelectionDialogState((currentValue) => ({
                  ...currentValue,
                  selectionsByUserId: {
                    ...currentValue.selectionsByUserId,
                    [String(candidate.userId)]: String(event.target.value),
                  },
                }))}
              >
                {candidate.events.map((event) => (
                  <MenuItem key={`assessment-selection-option-${candidate.userId}-${event.eventId}`} value={String(event.eventId)}>
                    {formatAssessmentSelectionLabel(event)}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssessmentSelectionDialog} disabled={exportingPdf}>Abbrechen</Button>
          <Button variant="contained" onClick={handleConfirmAssessmentSelectionDialog} disabled={exportingPdf}>
            Auswahl übernehmen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeExamDialogOpen} onClose={() => setCloseExamDialogOpen(false)}>
        <DialogTitle>Prüfung abschließen</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedExam ? `Soll die Prüfung „${selectedExam.label}“ wirklich abgeschlossen werden? Anschließend sind Änderungen erst nach einer Wiederöffnung mit Begründung möglich.` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseExamDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="success" onClick={handleCloseExam} disabled={busy || !canCloseSelectedExam}>
            Abschließen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unlockExamDialogOpen} onClose={() => setUnlockExamDialogOpen(false)}>
        <DialogTitle>Prüfung wieder öffnen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1, minWidth: { xs: 'auto', sm: 420 } }}>
            <Typography>
              {selectedExam ? `Bitte begründen Sie, warum die abgeschlossene Prüfung „${selectedExam.label}“ erneut bearbeitet werden muss.` : ''}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Begründung"
              value={examUnlockReason}
              onChange={(event) => setExamUnlockReason(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockExamDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="warning" onClick={handleUnlockExam} disabled={busy || !examUnlockReason.trim()}>
            Wieder öffnen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteExamDialogOpen} onClose={() => setDeleteExamDialogOpen(false)}>
        <DialogTitle>Prüfung löschen</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedExam ? `Soll die Prüfung „${selectedExam.label}“ wirklich gelöscht werden? Dieser Vorgang kann nicht rückgängig gemacht werden.` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteExamDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDeleteExam} disabled={busy || !canDeleteSelectedExam}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}